/**
 * Push Notification utilities for Community Deals.
 * Registers service worker, requests permission, and manages subscription.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function registerPushSubscription(userId) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.log("Push notifications not supported");
    return { supported: false };
  }

  try {
    // Register the service worker
    const registration = await navigator.serviceWorker.register("/sw-push.js");
    await navigator.serviceWorker.ready;

    // Request notification permission
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return { supported: true, permission: "denied" };
    }

    // Get VAPID public key
    let vapidKey = VAPID_PUBLIC_KEY;
    if (!vapidKey) {
      const res = await fetch(`${BACKEND_URL}/api/push/vapid-key`);
      if (res.ok) {
        const data = await res.json();
        vapidKey = data.public_key;
      }
    }

    if (!vapidKey) {
      console.error("No VAPID key available");
      return { supported: true, permission: "granted", subscribed: false };
    }

    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    // Send subscription to backend
    const res = await fetch(`${BACKEND_URL}/api/push/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: userId,
        subscription: subscription.toJSON(),
      }),
    });

    return {
      supported: true,
      permission: "granted",
      subscribed: res.ok,
    };
  } catch (err) {
    console.error("Push registration failed:", err);
    return { supported: true, permission: "unknown", subscribed: false, error: err.message };
  }
}

export async function unregisterPushSubscription(userId) {
  try {
    await fetch(`${BACKEND_URL}/api/push/unsubscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    });

    const registration = await navigator.serviceWorker.getRegistration("/sw-push.js");
    if (registration) {
      const sub = await registration.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
    }

    return { success: true };
  } catch (err) {
    console.error("Push unregistration failed:", err);
    return { success: false, error: err.message };
  }
}

export function isPushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window;
}
